import React from 'react';
import { Image, Spin } from 'antd';
import AppModal from '@/pages/inside/tenant/AppModal';
import { GlobalImagePreviewStyles } from '@/components/ui/GlobalImagePreviewStyles';
import { Markdown } from '@/components/ui/Markdown';
import { Button } from '@/components/ui/Button';
import TaskAttachmentTile from '@/components/module/projects/task/TaskAttachmentTile';
import { Container } from '@/components/ui/Container';
import { useIssueUpgradeDetailModal } from '@hooks/projects/useIssueUpgradeDetailModal';
import { getIssueOrUpgradeDisplayId } from '@hooks/projects/useProjectIssuesAndUpgrades';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ModalChromeHeading,
    ModalBlockLabel,
    MutedText,
    CardDescriptionInner,
    PanelSectionLabel,
    BoardUnderlineTabRow,
    BoardUnderlineTabInner,
    BoardUnderlineTab,
    BoardUnderlineTabLine,
    BoardUnderlineTabPrimaryLabel,
    BoardUnderlineTabCountBadge,
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    AttachmentCardsRow,
} from '@/pages/inside/tenant/style';
import { SprintTaskCard } from '@/components/sprint/SprintTaskCard';
import { FileOutlined, LinkOutlined, UnorderedListOutlined } from '@ant-design/icons';

function normalizeIauLinkedTaskForSprintCard(row) {
    if (row == null || typeof row !== 'object') return row;
    return {
        ...row,
        id: row.sprintTaskId ?? row.id ?? row.taskId,
        taskId: row.taskId ?? row.id,
        status: row.sprintTaskStatus ?? row.status,
        title: row.title ?? row.name,
        name: row.name ?? row.title,
    };
}

function iauLinkedTaskKanbanColumn(row) {
    const s = String(row?.sprintTaskStatus ?? row?.status ?? '').toLowerCase();
    if (['done', 'completed'].includes(s)) return 'completed';
    if (['submitted', 'submitted_to_qa', 'submitted_to_pm'].includes(s)) return 'submitted';
    if (s === 'bugs') return 'bugs';
    return 'todo';
}

/**
 * Issue/upgrade detail: matches {@link TaskDetailModal} chrome (description block + underline Attachments tab + Tasks tab).
 */
export default function IssueUpgradeDetailModal({ projectId, item, onClose, onOpenLinkedSprintTask }) {
    const vm = useIssueUpgradeDetailModal(projectId, item);

    const open = Boolean(item);
    const displayId = item ? getIssueOrUpgradeDisplayId(item) : '';

    const title =
        item && open ? (
            <Container $display="flex" $flexDirection="column" $gap="4px">
                <MutedText style={{ fontSize: 12, margin: 0 }}>{displayId}</MutedText>
                <ModalChromeHeading style={{ fontSize: 20 }}>
                    {String(item?.title ?? '').trim() || (vm.taskTypeLabel === 'Upgrade' ? 'Upgrade' : 'Issue')}
                </ModalChromeHeading>
            </Container>
        ) : null;

    return (
        <>
            <GlobalImagePreviewStyles />
            <AppModal
                size="lg"
                lgWidth="min(1180px, calc(100vw - 48px))"
                open={open}
                onCancel={onClose}
                title={title}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button type="button" $primary={false} $size="14px" onClick={onClose}>
                            Close
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                {open && item ? (
                    <CreateRoleModalBody
                        $flush
                        style={{
                            height: '70vh',
                            maxHeight: '70vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Container $display="flex" $flexDirection="column" $gap="16px" style={{ flex: 1, minHeight: 0 }}>
                            <div>
                                <Container $display="flex" $alignItems="center" $justifyContent="space-between" $gap="12px">
                                    <ModalBlockLabel as="div" style={{ display: 'block', marginBottom: 8 }}>
                                        Description
                                    </ModalBlockLabel>
                                    <span
                                        style={{
                                            marginBottom: 8,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '4px 10px',
                                            borderRadius: 999,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                            background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                        }}
                                    >
                                        {vm.taskTypeLabel}
                                    </span>
                                </Container>
                                {vm.hasDescription ? (
                                    <div style={{ maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
                                        <Markdown
                                            source={vm.descriptionMarkdown}
                                            component={CardDescriptionInner}
                                            richInlineAnchors
                                            linkPrimary
                                            attachmentItems={vm.descriptionAttachmentItems}
                                            onLinkClick={(payload) =>
                                                vm.handleMarkdownImageLinkClick(payload, vm.descriptionAttachmentItems)
                                            }
                                            emptyFallback="—"
                                        />
                                    </div>
                                ) : (
                                    <MutedText style={{ margin: 0 }}>No description</MutedText>
                                )}
                                {vm.reasonMarkdown ? (
                                    <div style={{ marginTop: 12 }}>
                                        <ModalBlockLabel as="div" style={{ display: 'block', marginBottom: 8 }}>
                                            Reason
                                        </ModalBlockLabel>
                                        <div style={{ maxHeight: 120, overflowY: 'auto', paddingRight: 4 }}>
                                            <Markdown
                                                source={vm.reasonMarkdown}
                                                component={CardDescriptionInner}
                                                variant="compact"
                                            />
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <BoardUnderlineTabRow style={{ marginTop: 4, marginBottom: 0 }}>
                                <BoardUnderlineTabInner style={{ paddingLeft: 0, paddingRight: 0 }}>
                                    <BoardUnderlineTab
                                        type="button"
                                        $active={vm.detailTab === 'attachments'}
                                        onClick={() => vm.setDetailTab('attachments')}
                                    >
                                        <BoardUnderlineTabLine>
                                            <BoardUnderlineTabPrimaryLabel
                                                $active={vm.detailTab === 'attachments'}
                                                style={{ fontSize: 12 }}
                                            >
                                                Attachments
                                            </BoardUnderlineTabPrimaryLabel>
                                            <BoardUnderlineTabCountBadge $active={vm.detailTab === 'attachments'}>
                                                {vm.attachmentCount}
                                            </BoardUnderlineTabCountBadge>
                                        </BoardUnderlineTabLine>
                                    </BoardUnderlineTab>
                                    <BoardUnderlineTab
                                        type="button"
                                        $active={vm.detailTab === 'tasks'}
                                        onClick={() => vm.setDetailTab('tasks')}
                                    >
                                        <BoardUnderlineTabLine>
                                            <BoardUnderlineTabPrimaryLabel
                                                $active={vm.detailTab === 'tasks'}
                                                style={{ fontSize: 12 }}
                                            >
                                                Tasks
                                            </BoardUnderlineTabPrimaryLabel>
                                            <BoardUnderlineTabCountBadge $active={vm.detailTab === 'tasks'}>
                                                {vm.tasksList?.length ?? 0}
                                            </BoardUnderlineTabCountBadge>
                                        </BoardUnderlineTabLine>
                                    </BoardUnderlineTab>
                                </BoardUnderlineTabInner>
                            </BoardUnderlineTabRow>

                            <div style={{ paddingRight: 4, flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                {vm.detailTab === 'attachments' && (
                                    <Container $display="flex" $flexDirection="column" $gap="10px" style={{ minHeight: '100%' }}>
                                        {vm.attachmentCount === 0 ? (
                                            <EmptyStateBlock style={{ minHeight: '100%', padding: '36px 24px' }}>
                                                <EmptyStateIcon>
                                                    <FileOutlined />
                                                </EmptyStateIcon>
                                                <EmptyStateTitle>No attachments</EmptyStateTitle>
                                                <EmptyStateDescription>
                                                    Files and links appear here when attached to this issue or upgrade.
                                                </EmptyStateDescription>
                                            </EmptyStateBlock>
                                        ) : (
                                            <>
                                                {vm.attachmentLinksPresentation.length > 0 ? (
                                                    <Container $display="flex" $flexDirection="column" $gap="8px">
                                                        <PanelSectionLabel
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 6,
                                                                marginBottom: 0,
                                                                fontSize: 11,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.04em',
                                                                opacity: 0.75,
                                                            }}
                                                        >
                                                            <LinkOutlined />
                                                            Links
                                                        </PanelSectionLabel>
                                                        <Container $display="flex" $flexWrap="wrap" $gap="8px">
                                                            {vm.attachmentLinksPresentation.map((lnk) => (
                                                                <a
                                                                    key={lnk.id}
                                                                    href={lnk.href || '#'}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {lnk.title}
                                                                </a>
                                                            ))}
                                                        </Container>
                                                    </Container>
                                                ) : null}
                                                {vm.attachmentFilesPresentation.length > 0 ? (
                                                    <Container $display="flex" $flexDirection="column" $gap="8px">
                                                        <PanelSectionLabel
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 6,
                                                                marginBottom: 0,
                                                                fontSize: 11,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.04em',
                                                                opacity: 0.75,
                                                            }}
                                                        >
                                                            <FileOutlined />
                                                            Files
                                                        </PanelSectionLabel>
                                                        <AttachmentCardsRow>
                                                            {vm.attachmentFilesPresentation.map(({ item: attItem, href, isImage, ext }, idx) => (
                                                                <TaskAttachmentTile
                                                                    key={`${attItem.id ?? 'att'}-${idx}`}
                                                                    href={href}
                                                                    title={attItem.name}
                                                                    isImage={isImage}
                                                                    ext={ext}
                                                                    onPreviewImage={(url) => vm.setDescriptionPreviewImage(url)}
                                                                    onOpenFile={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
                                                                />
                                                            ))}
                                                        </AttachmentCardsRow>
                                                    </Container>
                                                ) : null}
                                            </>
                                        )}
                                    </Container>
                                )}
                                {vm.detailTab === 'tasks' && (
                                    <Container $display="flex" $flexDirection="column" $gap="12px" style={{ minHeight: '100%' }}>
                                        {vm.tasksShowLoading ? (
                                            <Container
                                                $display="flex"
                                                $justifyContent="center"
                                                $alignItems="center"
                                                style={{ padding: '40px 0' }}
                                            >
                                                <Spin size="large" />
                                            </Container>
                                        ) : (vm.tasksList?.length ?? 0) === 0 ? (
                                            <EmptyStateBlock style={{ minHeight: '100%', padding: '36px 24px' }}>
                                                <EmptyStateIcon>
                                                    <UnorderedListOutlined />
                                                </EmptyStateIcon>
                                                <EmptyStateTitle>No linked tasks yet</EmptyStateTitle>
                                                <EmptyStateDescription>
                                                    Tasks from this issue or upgrade are loaded from the linked-tasks endpoint when you
                                                    open this panel. None were returned, and this item has no embedded tasks on the list.
                                                </EmptyStateDescription>
                                            </EmptyStateBlock>
                                        ) : (
                                            <Container $display="flex" $flexDirection="column" $gap="12px">
                                                {(vm.tasksList || []).map((t, idx) => {
                                                    const taskForCard = normalizeIauLinkedTaskForSprintCard(t);
                                                    const bugs = Array.isArray(t?.bugs) ? t.bugs : [];
                                                    const col = iauLinkedTaskKanbanColumn(t);
                                                    return (
                                                        <div
                                                            key={String(t?.taskId ?? t?.sprintTaskId ?? t?.id ?? idx)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onPointerDown={(e) => e.stopPropagation()}
                                                            role="presentation"
                                                        >
                                                            <SprintTaskCard
                                                                task={taskForCard}
                                                                variant="readOnly"
                                                                kanbanColumn={col}
                                                                kanbanColumnTintOnly
                                                                bugs={bugs}
                                                                suppressOverflowMenu
                                                                onOpenDetail={
                                                                    typeof onOpenLinkedSprintTask === 'function'
                                                                        ? () =>
                                                                              onOpenLinkedSprintTask(
                                                                                  normalizeIauLinkedTaskForSprintCard(t),
                                                                              )
                                                                        : undefined
                                                                }
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </Container>
                                        )}
                                    </Container>
                                )}
                            </div>
                        </Container>
                    </CreateRoleModalBody>
                ) : null}
                <Image
                    style={{ display: 'none' }}
                    src={vm.descriptionPreviewImage || undefined}
                    preview={{
                        visible: !!vm.descriptionPreviewImage,
                        src: vm.descriptionPreviewImage || undefined,
                        rootClassName: 'global-image-preview-root',
                        onVisibleChange: (visible) => {
                            if (!visible) vm.setDescriptionPreviewImage('');
                        },
                    }}
                />
            </AppModal>
        </>
    );
}
